import { Injectable } from '@angular/core';
import { AbstractCRUDService, ExtendableRefBookService } from '@campaign-portal/components-library';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { UsersAndRolesService } from '@helpers/api/users-and-roles.service';
import {
	PermissionsReadResponse,
	RolesCreateResponse, RolesDeleteRequest, RolesDeleteResponse,
	RolesReadRequest,
	RolesReadResponse, RolesUpdateResponse
} from '@helpers/types/user-and-roles';
import { Role } from '@helpers/types/models';
import { ExportService } from '@helpers/api/export.service';
import { RolesFieldsService } from './roles.fields.service';
import { exist } from '@campaign-portal/namespace/common/id';
import { ReadResponse } from '@campaign-portal/namespace/common/implementations';

@Injectable({
	providedIn: 'root'
})
export class RolesService extends ExtendableRefBookService<Role<exist>> implements AbstractCRUDService {

	override readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	readonly entity = '';
	readonly title = '';
	readonly create = this.update;

	constructor(
		private readonly usersAndRoles: UsersAndRolesService,
		private readonly fields: RolesFieldsService,
		private readonly exportService: ExportService
	) {
		super();
	}

	override load(): Observable<ReadResponse<Role<number>[]>> {
		return this.usersAndRoles.loader<ReadResponse<Role<number>[]>>('Roles.Read', {}, this.loading$)
			.pipe(
				map((resp) => {
					return {
						Success: true,
						Total: resp.Total,
						Data: resp.Data
					};
				}),
				map((resp) => {
					return this.process(resp);
				})
			);
	}

	override process(resp: ReadResponse<Role<number>[]>): ReadResponse<Role<number>[]> {
		this.list$.next(resp.Data);
		this.map$.next(this.list.reduce((result, item) => {
			result.set(item.ID, item);
			return result;
		}, new Map()));
		return resp;
	}

	read(params?: RolesReadRequest): Observable<RolesReadResponse> {
		if (params) {
			const updatedParams = {
				...params,
				Sorting: [
					...(params.Sorting ?? []),
					{
						Field: 'ID',
						Dir: 'desc'
					}
				]
			};
			return this.usersAndRoles.loader<RolesReadResponse>('Roles.Read', updatedParams, this.loading$);
		} else {
			const updatedParams = {
				Sorting: [
					{
						Field: 'ID',
						Dir: 'desc'
					}
				]
			};
			return this.usersAndRoles.loader<RolesReadResponse>('Roles.Read', updatedParams, this.loading$);
		}
	}

	update(params: Role): Observable<RolesUpdateResponse | RolesCreateResponse> {
		const method = params.ID ? 'Roles.Update' : 'Roles.Create';
		return this.usersAndRoles.loader<RolesUpdateResponse | RolesCreateResponse>(method, params, this.loading$);
	}

	delete(params: RolesDeleteRequest): Observable<RolesDeleteResponse> {
		return this.usersAndRoles.loader<RolesDeleteResponse>('Roles.Delete', params, this.loading$);
	}

	permissions(): Observable<PermissionsReadResponse> {
		return this.usersAndRoles.loader<PermissionsReadResponse>('Permissions.Read');
	}

	export(total: number): Observable<void> {
		return this.exportService.export(
			this.usersAndRoles.userAndRolesApi.url,
			'Roles.Read',
			{
				Paging: {
					Skip: 0,
					Take: total
				}
			},
			this.fields.headers,
			'roles'
		);
	}

}
